/**
 * Panels — the declarative content shape, and a validator for plugins that store
 * panels as DATA.
 *
 * A panel is a bounded block of content (a title, ordered leaf elements, some
 * buttons) rendered by `@fias/panel-kit`, which the platform serves to plugin
 * iframes from its own CDN (declare the `sandbox:vendored-libraries` permission
 * and list `"@fias/panel-kit"` in your manifest `dependencies`). See
 * `templates/default/CLAUDE.md` § Panels.
 *
 * WHAT THIS MODULE IS FOR. Rendering needs no help from the SDK — panel-kit takes
 * a plain object. What the SDK adds is the part a plugin cannot do for itself:
 *
 *   1. **The types**, so a plugin written against the published npm package has
 *      the same shape the platform validates, without depending on panel-kit's
 *      private workspace package for type information.
 *   2. **`validatePanelCatalog`**, so a plugin that keeps its panels as data —
 *      in `useFiasDataStore`, in a config document, in an admin-edited blob —
 *      can gate a WRITE the way the platform gates its own server-authored
 *      catalogs, instead of discovering a malformed panel when a player sees a
 *      blank one.
 *
 * That second point is the whole design: **panels stored as data need no new
 * platform op.** A plugin already has generic storage; what it lacked was the
 * shared definition of "valid". Nothing here talks to the platform, nothing is
 * gated, nothing is billed — it is a pure function over a value you already hold,
 * which is why it can live in the SDK rather than behind a bridge call.
 *
 * MIRROR, NOT A FORK. The canonical schema is `panelThemeSchema` /
 * `panelElementSchema` in `packages/db-types/src/panel-definition.ts` (Zod,
 * server-side). This is a hand-written mirror for the same reason panel-kit's
 * types are: `@fias/db-types` is a server package that would drag zod and a large
 * unrelated surface into every plugin bundle. `tests/architecture/panel-schema-mirror.test.ts`
 * runs the canonical corpora against BOTH mirrors, so a grammar cannot drift here
 * without failing CI.
 *
 * A renderer older than a definition SKIPS element kinds it does not know. This
 * validator is deliberately stricter than that: an unknown kind is an ERROR, so an
 * authoring mistake fails at write time rather than rendering as silence.
 */

/** Element list bounds. Mirrors `PANEL_*` in db-types. */
export const PANEL_MIN_ELEMENTS = 1;
export const PANEL_MAX_ELEMENTS = 24;
export const PANEL_TITLE_MAX_LENGTH = 80;
export const PANEL_TEXT_MAX_LENGTH = 500;
export const PANEL_IMAGE_ALT_MAX_LENGTH = 120;
export const PANEL_IMAGE_KEY_MAX_LENGTH = 500;
export const PANEL_IMAGE_MIN_HEIGHT = 24;
export const PANEL_IMAGE_MAX_HEIGHT = 512;
export const PANEL_BUTTON_LABEL_MAX_LENGTH = 40;
export const PANEL_ACTION_MAX_LENGTH = 2048;
export const PANEL_EMIT_TOKEN_MAX_LENGTH = 64;
export const PANEL_ITEMS_EMPTY_TEXT_MAX_LENGTH = 120;
export const PANEL_ITEMS_SLOT_MAX_LENGTH = 64;
export const PANEL_SHOW_IF_MAX_LENGTH = 64;
export const PANEL_PRIORITY_ABS_MAX = 1000;
export const PANEL_THEME_FONT_MAX_LENGTH = 200;
export const PANEL_THEME_AMOUNT_FORMAT_MAX_LENGTH = 24;
export const PANEL_THEME_Z_MAX = 1_000_000;
export const PANEL_THEME_LENGTH_MAX_INTEGER_DIGITS = 4;

/**
 * Storage roots the platform will presign inside a panel image key. A key outside
 * them is not merely unusual — it is one the platform will not resolve, so it
 * would render as a permanently missing image.
 */
export const PANEL_IMAGE_ROOTS = ['dfe-icons', 'dfe-avatars'] as const;

/** Leaf kinds only — panels do not nest. Order matches the canonical union. */
export const PANEL_ELEMENT_KINDS = [
  'text',
  'conditionalText',
  'image',
  'assetImage',
  'items',
  'slottedItems',
  'button',
  'spacer',
] as const;
export type PanelElementKind = (typeof PANEL_ELEMENT_KINDS)[number];

export type PanelTextStyle = 'title' | 'body' | 'caption';
export type PanelTextAlign = 'left' | 'center' | 'right';
export type PanelSize = 'small' | 'medium' | 'large';

interface PanelTextFields {
  text: string;
  style?: PanelTextStyle;
  align?: PanelTextAlign;
  /** Draw on the same line as the element that follows. Purely a line-break hint. */
  joinNext?: boolean;
}

export interface PanelTextElement extends PanelTextFields {
  kind: 'text';
}

/** Drawn only when `context.values[showIf]` is truthy. `showIf` is a value NAME, never an expression. */
export interface PanelConditionalTextElement extends PanelTextFields {
  kind: 'conditionalText';
  showIf: string;
}

export interface PanelImageElement {
  kind: 'image';
  /** Relative platform-storage key under {@link PANEL_IMAGE_ROOTS}; the platform presigns it. */
  imageKey: string;
  alt?: string;
  maxHeight?: number;
}

export interface PanelAssetImageElement {
  kind: 'assetImage';
  /** `as_<32 hex>` — an arche asset-library id. */
  assetId: string;
  alt?: string;
  maxHeight?: number;
}

export interface PanelItemsElement {
  kind: 'items';
  showIcons?: boolean;
  iconSize?: PanelSize;
  emptyText?: string;
}

/** Draws `context.itemsBySlot[slot]` — one panel, several independent lists. */
export interface PanelSlottedItemsElement {
  kind: 'slottedItems';
  slot: string;
  showIcons?: boolean;
  iconSize?: PanelSize;
  emptyText?: string;
}

export interface PanelButtonElement {
  kind: 'button';
  label: string;
  /** `close` | `emit:<token>` | `open_arche:arc_<32 hex>` | `open_url:https://…` */
  action: string;
  variant?: 'primary' | 'secondary';
  /**
   * Arche asset id (`as_<32 hex>`) for a small image drawn BESIDE the label.
   * The label stays required — an icon-only button has no reading on a renderer
   * that predates this field.
   */
  icon?: string;
}

export interface PanelSpacerElement {
  kind: 'spacer';
  size?: PanelSize;
}

export type PanelElement =
  | PanelTextElement
  | PanelConditionalTextElement
  | PanelImageElement
  | PanelAssetImageElement
  | PanelItemsElement
  | PanelSlottedItemsElement
  | PanelButtonElement
  | PanelSpacerElement;

/** An authored hint about presentation; the call site may override any of it. */
/** How a MODAL panel enters. Inline panels do not animate. */
export interface PanelAnimation {
  type?: 'fade-rise' | 'fade' | 'none';
  /** 0–2000. */
  durationMs?: number;
  easing?: 'ease-out' | 'ease-in' | 'ease-in-out' | 'linear';
}

export interface PanelPresentation {
  size?: PanelSize;
  dismissible?: boolean;
  priority?: number;
  /** Omitted means the entrance every renderer already draws. */
  animation?: PanelAnimation;
}

export interface PanelDefinition {
  /** 2–64 chars: lowercase letters, digits and hyphens, not starting with a hyphen. */
  id: string;
  title: string;
  elements: PanelElement[];
  presentation?: PanelPresentation;
}

/** Colours, font, radii, spacing and quantity format for a whole catalog. */
export interface PanelTheme {
  bg?: string;
  fg?: string;
  border?: string;
  accent?: string;
  accentFg?: string;
  scrim?: string;
  font?: string;
  radius?: string;
  buttonRadius?: string;
  gap?: string;
  fontSize?: string;
  widthSmall?: string;
  widthMedium?: string;
  widthLarge?: string;
  z?: number;
  /** Must contain `{n}` — `×{n}` renders 25 as `×25`. */
  amountFormat?: string;
}

/** A plugin's own stored panels: the definitions plus one optional theme. */
export interface PanelCatalog {
  panels: PanelDefinition[];
  theme?: PanelTheme;
}

/** `ok: false` carries one human-readable issue per problem, each prefixed by its path. */
export type PanelValidationResult = { ok: true; issues: [] } | { ok: false; issues: string[] };

const PANEL_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,63}$/;
const SLOT_PATTERN = new RegExp(`^[a-z0-9][a-z0-9_-]{0,${PANEL_ITEMS_SLOT_MAX_LENGTH - 1}}$`);
const EMIT_TOKEN_PATTERN = new RegExp(`^[a-z0-9][a-z0-9_-]{0,${PANEL_EMIT_TOKEN_MAX_LENGTH - 1}}$`);
const SHOW_IF_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const ASSET_ID_PATTERN = /^as_[a-f0-9]{32}$/;
const ARCHE_ID_PATTERN = /^arc_[a-f0-9]{32}$/;
const IMAGE_KEY_PATTERN = new RegExp(
  `^(?:${PANEL_IMAGE_ROOTS.join('|')})/[A-Za-z0-9._/-]+\\.(?:png|jpe?g|webp|gif|svg)$`,
  'i',
);
const THEME_COLOR_PATTERN =
  /^(?:#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d{1,3})\s*)?\))$/i;
const THEME_LENGTH_PATTERN = new RegExp(
  `^\\d{1,${PANEL_THEME_LENGTH_MAX_INTEGER_DIGITS}}(?:\\.\\d{1,3})?(?:px|rem|em)$`,
);
const THEME_FONT_PATTERN = /^[A-Za-z0-9 '",._-]+$/;

const TEXT_STYLES: readonly string[] = ['title', 'body', 'caption'];
const TEXT_ALIGNS: readonly string[] = ['left', 'center', 'right'];
const SIZES: readonly string[] = ['small', 'medium', 'large'];
const BUTTON_VARIANTS: readonly string[] = ['primary', 'secondary'];

type Issues = string[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `.strict()`, mirrored: an unrecognized field is a typo that would silently do nothing. */
function rejectUnknownKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
  issues: Issues,
): void {
  for (const key of Object.keys(value)) {
    if (!allowed.includes(key)) issues.push(`${path}: unknown field "${key}"`);
  }
}

function checkString(
  value: unknown,
  path: string,
  issues: Issues,
  opts: { min?: number; max: number; pattern?: RegExp; hint?: string },
): void {
  if (typeof value !== 'string') {
    issues.push(`${path}: expected a string`);
    return;
  }
  if (value.length < (opts.min ?? 1)) issues.push(`${path}: must not be empty`);
  if (value.length > opts.max) issues.push(`${path}: longer than ${opts.max} characters`);
  if (opts.pattern && !opts.pattern.test(value)) {
    issues.push(`${path}: ${opts.hint ?? 'does not match the required format'}`);
  }
}

function checkOptionalEnum(
  value: unknown,
  path: string,
  issues: Issues,
  allowed: readonly string[],
): void {
  if (value === undefined) return;
  if (typeof value !== 'string' || !allowed.includes(value)) {
    issues.push(`${path}: must be one of ${allowed.join(' | ')}`);
  }
}

function checkOptionalBoolean(value: unknown, path: string, issues: Issues): void {
  if (value !== undefined && typeof value !== 'boolean') issues.push(`${path}: expected a boolean`);
}

function checkOptionalIntRange(
  value: unknown,
  path: string,
  issues: Issues,
  min: number,
  max: number,
): void {
  if (value === undefined) return;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    issues.push(`${path}: must be an integer between ${min} and ${max}`);
  }
}

/**
 * The closed action namespace. `open_url` is checked by PARSING the URL, not by a
 * regex, so `javascript:`, `data:`, protocol-relative and whitespace-obfuscated
 * forms are rejected the same way the platform rejects them.
 */
export function validatePanelAction(action: unknown, path: string, issues: Issues): void {
  if (typeof action !== 'string' || action.length === 0) {
    issues.push(`${path}: expected an action string`);
    return;
  }
  if (action.length > PANEL_ACTION_MAX_LENGTH) {
    issues.push(`${path}: longer than ${PANEL_ACTION_MAX_LENGTH} characters`);
    return;
  }
  if (action === 'close') return;

  if (action.startsWith('emit:')) {
    if (!EMIT_TOKEN_PATTERN.test(action.slice('emit:'.length))) {
      issues.push(
        `${path}: emit token must be 1–${PANEL_EMIT_TOKEN_MAX_LENGTH} chars — lowercase letters, digits, hyphens and underscores, not starting with a separator`,
      );
    }
    return;
  }

  if (action.startsWith('open_arche:')) {
    if (!ARCHE_ID_PATTERN.test(action.slice('open_arche:'.length))) {
      issues.push(`${path}: open_arche target must be an arche id (arc_ + 32 hex chars)`);
    }
    return;
  }

  if (action.startsWith('open_url:')) {
    if (/\s/.test(action)) {
      issues.push(`${path}: action must not contain whitespace`);
      return;
    }
    let parsed: URL;
    try {
      parsed = new URL(action.slice('open_url:'.length));
    } catch {
      issues.push(`${path}: open_url must be a valid absolute URL`);
      return;
    }
    if (parsed.protocol !== 'https:') issues.push(`${path}: open_url scheme must be https`);
    return;
  }

  issues.push(
    `${path}: must be one of close | emit:<token> | open_arche:arc_<32 hex> | open_url:https://…`,
  );
}

function validateTextFields(element: Record<string, unknown>, path: string, issues: Issues): void {
  checkString(element.text, `${path}.text`, issues, { max: PANEL_TEXT_MAX_LENGTH });
  checkOptionalEnum(element.style, `${path}.style`, issues, TEXT_STYLES);
  checkOptionalEnum(element.align, `${path}.align`, issues, TEXT_ALIGNS);
  checkOptionalBoolean(element.joinNext, `${path}.joinNext`, issues);
}

function validateItemsFields(element: Record<string, unknown>, path: string, issues: Issues): void {
  checkOptionalBoolean(element.showIcons, `${path}.showIcons`, issues);
  checkOptionalEnum(element.iconSize, `${path}.iconSize`, issues, SIZES);
  if (element.emptyText !== undefined) {
    checkString(element.emptyText, `${path}.emptyText`, issues, {
      min: 0,
      max: PANEL_ITEMS_EMPTY_TEXT_MAX_LENGTH,
    });
  }
}

function validateImageFields(element: Record<string, unknown>, path: string, issues: Issues): void {
  if (element.alt !== undefined) {
    checkString(element.alt, `${path}.alt`, issues, { min: 0, max: PANEL_IMAGE_ALT_MAX_LENGTH });
  }
  checkOptionalIntRange(
    element.maxHeight,
    `${path}.maxHeight`,
    issues,
    PANEL_IMAGE_MIN_HEIGHT,
    PANEL_IMAGE_MAX_HEIGHT,
  );
}

function validateElement(value: unknown, path: string, issues: Issues): void {
  if (!isRecord(value)) {
    issues.push(`${path}: expected an object`);
    return;
  }
  const kind = value.kind;
  if (typeof kind !== 'string' || !(PANEL_ELEMENT_KINDS as readonly string[]).includes(kind)) {
    issues.push(
      `${path}.kind: unknown element kind ${JSON.stringify(kind)} — expected one of ${PANEL_ELEMENT_KINDS.join(' | ')}`,
    );
    return;
  }

  switch (kind as PanelElementKind) {
    case 'text':
      rejectUnknownKeys(value, ['kind', 'text', 'style', 'align', 'joinNext'], path, issues);
      validateTextFields(value, path, issues);
      return;
    case 'conditionalText':
      rejectUnknownKeys(
        value,
        ['kind', 'text', 'style', 'align', 'joinNext', 'showIf'],
        path,
        issues,
      );
      validateTextFields(value, path, issues);
      checkString(value.showIf, `${path}.showIf`, issues, {
        max: PANEL_SHOW_IF_MAX_LENGTH,
        pattern: SHOW_IF_PATTERN,
        hint: 'must be a value NAME (letters, digits, underscore; not starting with a digit) — expressions and operators are not supported',
      });
      return;
    case 'image':
      rejectUnknownKeys(value, ['kind', 'imageKey', 'alt', 'maxHeight'], path, issues);
      checkString(value.imageKey, `${path}.imageKey`, issues, {
        max: PANEL_IMAGE_KEY_MAX_LENGTH,
        pattern: IMAGE_KEY_PATTERN,
        hint: `must be a relative key under ${PANEL_IMAGE_ROOTS.join(' or ')}/ ending in an image extension (no URL, no leading slash)`,
      });
      if (typeof value.imageKey === 'string' && value.imageKey.includes('..')) {
        issues.push(`${path}.imageKey: must not contain ".."`);
      }
      validateImageFields(value, path, issues);
      return;
    case 'assetImage':
      rejectUnknownKeys(value, ['kind', 'assetId', 'alt', 'maxHeight'], path, issues);
      checkString(value.assetId, `${path}.assetId`, issues, {
        max: 64,
        pattern: ASSET_ID_PATTERN,
        hint: 'must be an arche asset id (as_ + 32 hex chars)',
      });
      validateImageFields(value, path, issues);
      return;
    case 'items':
      rejectUnknownKeys(value, ['kind', 'showIcons', 'iconSize', 'emptyText'], path, issues);
      validateItemsFields(value, path, issues);
      return;
    case 'slottedItems':
      rejectUnknownKeys(
        value,
        ['kind', 'slot', 'showIcons', 'iconSize', 'emptyText'],
        path,
        issues,
      );
      checkString(value.slot, `${path}.slot`, issues, {
        max: PANEL_ITEMS_SLOT_MAX_LENGTH,
        pattern: SLOT_PATTERN,
        hint: 'must be lowercase letters, digits, hyphens and underscores, not starting with a separator',
      });
      validateItemsFields(value, path, issues);
      return;
    case 'button':
      rejectUnknownKeys(value, ['kind', 'label', 'action', 'variant', 'icon'], path, issues);
      checkString(value.label, `${path}.label`, issues, { max: PANEL_BUTTON_LABEL_MAX_LENGTH });
      validatePanelAction(value.action, `${path}.action`, issues);
      checkOptionalEnum(value.variant, `${path}.variant`, issues, BUTTON_VARIANTS);
      // Decoration beside the label, never instead of it — the label stays
      // required, which is what makes this safe for a renderer that predates it.
      if (value.icon !== undefined) {
        checkString(value.icon, `${path}.icon`, issues, {
          max: 64,
          pattern: ASSET_ID_PATTERN,
          hint: 'must be an arche asset id (as_ + 32 hex chars)',
        });
      }
      return;
    case 'spacer':
      rejectUnknownKeys(value, ['kind', 'size'], path, issues);
      checkOptionalEnum(value.size, `${path}.size`, issues, SIZES);
      return;
  }
}

const ANIMATION_TYPES = ['fade-rise', 'fade', 'none'] as const;
const ANIMATION_EASINGS = ['ease-out', 'ease-in', 'ease-in-out', 'linear'] as const;
const ANIMATION_MAX_DURATION_MS = 2000;

/**
 * How a MODAL panel enters. Enums and a bounded integer — never a CSS string,
 * for the same reason the theme's grammars are grammars: the value is read by
 * every renderer that draws this catalog, so one malformed entry is the whole
 * fleet at once.
 */
function validateAnimation(value: unknown, path: string, issues: Issues): void {
  if (value === undefined) return;
  if (!isRecord(value)) {
    issues.push(`${path}: expected an object`);
    return;
  }
  rejectUnknownKeys(value, ['type', 'durationMs', 'easing'], path, issues);
  checkOptionalEnum(value.type, `${path}.type`, issues, ANIMATION_TYPES);
  checkOptionalIntRange(value.durationMs, `${path}.durationMs`, issues, 0, ANIMATION_MAX_DURATION_MS);
  checkOptionalEnum(value.easing, `${path}.easing`, issues, ANIMATION_EASINGS);
}

const THEME_COLOR_FIELDS = ['bg', 'fg', 'border', 'accent', 'accentFg', 'scrim'] as const;
const THEME_LENGTH_FIELDS = [
  'radius',
  'buttonRadius',
  'gap',
  'fontSize',
  'widthSmall',
  'widthMedium',
  'widthLarge',
] as const;

/**
 * Validate an authored theme.
 *
 * The grammars are tight on purpose, and the reason is not style policing: a theme
 * value becomes a CSS custom property, and a custom property can otherwise carry
 * `url(…)` — an outbound request from every surface that draws one of your panels
 * — or a `;` that escapes its declaration. Colours are hex or `rgb()/rgba()`,
 * lengths are `px`/`rem`/`em`, and a font stack admits no parentheses at all.
 */
export function validatePanelTheme(value: unknown, path = 'theme'): PanelValidationResult {
  const issues: Issues = [];
  if (!isRecord(value)) return { ok: false, issues: [`${path}: expected an object`] };

  rejectUnknownKeys(
    value,
    [...THEME_COLOR_FIELDS, ...THEME_LENGTH_FIELDS, 'font', 'z', 'amountFormat'],
    path,
    issues,
  );

  for (const field of THEME_COLOR_FIELDS) {
    if (value[field] === undefined) continue;
    checkString(value[field], `${path}.${field}`, issues, {
      max: 64,
      pattern: THEME_COLOR_PATTERN,
      hint: 'must be a hex colour (#rgb, #rgba, #rrggbb, #rrggbbaa) or rgb()/rgba() with numeric components',
    });
  }
  for (const field of THEME_LENGTH_FIELDS) {
    if (value[field] === undefined) continue;
    checkString(value[field], `${path}.${field}`, issues, {
      max: 16,
      pattern: THEME_LENGTH_PATTERN,
      hint: `must be a non-negative number (max ${PANEL_THEME_LENGTH_MAX_INTEGER_DIGITS} digits before the point) followed by px, rem or em`,
    });
  }
  if (value.font !== undefined) {
    checkString(value.font, `${path}.font`, issues, {
      max: PANEL_THEME_FONT_MAX_LENGTH,
      pattern: THEME_FONT_PATTERN,
      hint: 'must be a font-family stack (names, commas, quotes) — no parentheses, so no url() or var()',
    });
  }
  checkOptionalIntRange(value.z, `${path}.z`, issues, 0, PANEL_THEME_Z_MAX);
  if (value.amountFormat !== undefined) {
    checkString(value.amountFormat, `${path}.amountFormat`, issues, {
      max: PANEL_THEME_AMOUNT_FORMAT_MAX_LENGTH,
    });
    if (typeof value.amountFormat === 'string' && !value.amountFormat.includes('{n}')) {
      issues.push(`${path}.amountFormat: must contain the placeholder {n}`);
    }
  }

  return issues.length === 0 ? { ok: true, issues: [] } : { ok: false, issues };
}

/** Validate one panel definition. */
export function validatePanelDefinition(value: unknown, path = 'panel'): PanelValidationResult {
  const issues: Issues = [];
  if (!isRecord(value)) return { ok: false, issues: [`${path}: expected an object`] };

  rejectUnknownKeys(value, ['id', 'title', 'elements', 'presentation'], path, issues);
  checkString(value.id, `${path}.id`, issues, {
    max: 64,
    pattern: PANEL_ID_PATTERN,
    hint: 'must be 2–64 chars: lowercase letters, digits and hyphens, not starting with a hyphen',
  });
  checkString(value.title, `${path}.title`, issues, { max: PANEL_TITLE_MAX_LENGTH });

  if (!Array.isArray(value.elements)) {
    issues.push(`${path}.elements: expected an array`);
  } else {
    if (value.elements.length < PANEL_MIN_ELEMENTS) {
      issues.push(`${path}.elements: needs at least ${PANEL_MIN_ELEMENTS} element`);
    }
    if (value.elements.length > PANEL_MAX_ELEMENTS) {
      issues.push(`${path}.elements: more than ${PANEL_MAX_ELEMENTS} elements`);
    }
    value.elements.forEach((element, index) =>
      validateElement(element, `${path}.elements[${index}]`, issues),
    );
  }

  if (value.presentation !== undefined) {
    if (!isRecord(value.presentation)) {
      issues.push(`${path}.presentation: expected an object`);
    } else {
      rejectUnknownKeys(
        value.presentation,
        ['size', 'dismissible', 'priority', 'animation'],
        `${path}.presentation`,
        issues,
      );
      checkOptionalEnum(value.presentation.size, `${path}.presentation.size`, issues, SIZES);
      checkOptionalBoolean(
        value.presentation.dismissible,
        `${path}.presentation.dismissible`,
        issues,
      );
      checkOptionalIntRange(
        value.presentation.priority,
        `${path}.presentation.priority`,
        issues,
        -PANEL_PRIORITY_ABS_MAX,
        PANEL_PRIORITY_ABS_MAX,
      );
      validateAnimation(value.presentation.animation, `${path}.presentation.animation`, issues);
    }
  }

  return issues.length === 0 ? { ok: true, issues: [] } : { ok: false, issues };
}

/**
 * Validate a whole catalog — `{ panels, theme? }` — before you STORE it.
 *
 * Call this on the write side (a DataStore document, a config blob, an admin form
 * submit), not on the read side. A renderer skipping an element it cannot draw is
 * a designed degrade; a panel that was never valid is an authoring bug, and the
 * difference between the two is which side of the write you find out on.
 *
 * ```ts
 * const result = validatePanelCatalog(draft);
 * if (!result.ok) return setErrors(result.issues);
 * await datastore.put('panels', draft);
 * ```
 *
 * Panel ids must be unique within a catalog: they are used for dedupe, telemetry
 * and render keys, so a duplicate is an authoring error rather than a
 * last-one-wins.
 */
export function validatePanelCatalog(value: unknown): PanelValidationResult {
  const issues: Issues = [];
  if (!isRecord(value)) return { ok: false, issues: ['catalog: expected an object'] };

  rejectUnknownKeys(value, ['panels', 'theme'], 'catalog', issues);

  if (value.theme !== undefined) {
    const theme = validatePanelTheme(value.theme, 'catalog.theme');
    if (!theme.ok) issues.push(...theme.issues);
  }

  if (!Array.isArray(value.panels)) {
    issues.push('catalog.panels: expected an array');
    return { ok: false, issues };
  }

  const seen = new Set<string>();
  value.panels.forEach((panel, index) => {
    const path = `catalog.panels[${index}]`;
    const result = validatePanelDefinition(panel, path);
    if (!result.ok) issues.push(...result.issues);
    if (isRecord(panel) && typeof panel.id === 'string') {
      if (seen.has(panel.id)) issues.push(`${path}.id: duplicate panel id "${panel.id}"`);
      seen.add(panel.id);
    }
  });

  return issues.length === 0 ? { ok: true, issues: [] } : { ok: false, issues };
}
